import { mapGetters } from 'vuex'
import { EventBus } from '~/helpers/miscs'

/**
 * Shared product-filter selection logic for the header "Filter Search" panels.
 *
 * Owns the `params` selection model, the brand <-> sub-brand checkbox
 * synchronisation, hydration from `$route.query`, and the apply-to-products
 * navigation. Consumed by the legacy `FilterSection` dropdown and the new
 * `MegaMenuFilterPanel` so both behave identically from a single source of
 * truth — the panels differ only in their template/layout.
 *
 * Lifted verbatim from FilterSection.vue; do not diverge the behaviour here
 * without checking both hosts.
 */
export default {
  computed: {
    ...mapGetters({
      filtersData: 'menus/filtersData'
    })
  },

  data () {
    return {
      stopWatcherPropagation: null,
      params: {
        brand: [],
        sub_brand: [],
        department: [],
        category: [],
        availability: [],
        color: [],
        pattern: [],
        season: [],
        criteria: 'set',
        quantity: null,
        size: []
      }
    }
  },

  created () {
    this.identifyFilters()
  },

  watch: {
    $route (to, from) {
      if (!this.stopWatcherPropagation) {
        this.stopWatcherPropagation = true
        const params = this.$route.query
        this.params.brand = (params.brand) ? params.brand.toString().split(',') : []
        this.params.sub_brand = (params.sub_brand) ? params.sub_brand.toString().split(',') : []
        this.params.department = (params.department) ? params.department.toString().split(',') : []
        this.params.category = (params.category) ? params.category.toString().split(',') : []
        this.params.availability = (params.availability) ? params.availability.toString().split(',') : []
        this.params.color = (params.color) ? params.color.toString().split(',') : []
        this.params.pattern = (params.pattern) ? params.pattern.toString().split(',') : []
        this.params.season = (params.season) ? params.season.toString().split(',') : []
        this.params.criteria = (params.criteria) ? params.criteria : 'set'
        this.params.quantity = (params.quantity) ? params.quantity : null
        this.params.size = (params.size) ? params.size : null
        setTimeout(() => {
          this.stopWatcherPropagation = false
        }, 100)
      }
    },
    'params.brand': function (newBrand, oldBrand) {
      if (!this.stopWatcherPropagation) {
        this.stopWatcherPropagation = true
        this.handleChildCheckboxChange(newBrand, oldBrand)
      }
    },
    'params.sub_brand': function (newSubBrand, oldSubBrand) {
      if (!this.stopWatcherPropagation) {
        this.stopWatcherPropagation = true
        this.handleParentCheckboxChange(newSubBrand, oldSubBrand)
      }
    },
    'params.availability': function (newAvailability, oldAvailability) {
      if (newAvailability.length > 1) {
        // Keep only the current entry and remove the previous ones
        this.params.availability = [newAvailability.pop()]
      }
    }
  },

  methods: {
    identifyFilters () {
      this.params = {
        brand: [],
        sub_brand: [],
        department: [],
        category: [],
        availability: [],
        color: [],
        pattern: [],
        season: [],
        quantity: null,
        criteria: 'set',
        size: []
      }
      this.stopWatcherPropagation = true
      const params = this.$route.query
      if (Object.keys(params).length > 0) {
        if (params.sub_brand) {
          this.params.sub_brand = params.sub_brand.toString().split(',').map(id => parseInt(id))
        }
        if (params.brand) {
          this.params.brand = params.brand.toString().split(',').map(id => parseInt(id))
        }
        if (params.department) {
          this.params.department = params.department.toString().split(',').map(id => parseInt(id))
        }
        if (params.category) {
          this.params.category = params.category.toString().split(',').map(id => parseInt(id))
        }
        if (params.color) {
          this.params.color = params.color.toString().split(',').map(id => parseInt(id))
        }
        if (params.pattern) {
          this.params.pattern = params.pattern.toString().split(',').map(id => parseInt(id))
        }
        if (params.season) {
          this.params.season = params.season.toString().split(',').map(id => parseInt(id))
        }
        if (params.availability) {
          this.params.availability = params.availability.toString().split(',').map(id => parseInt(id))
        }
        if (params.size) {
          this.params.size = params.size.toString().split(',').map(id => parseInt(id))
        }
        if (params.criteria && params.quantity) {
          this.params.criteria = (params.criteria) ? params.criteria : 'set'
          this.params.quantity = (params.quantity) ? params.quantity : null
        }
      }
      // Re-enable the sync watchers even when the route carries no query params
      // (e.g. the panel was opened from a non-products page). Leaving this inside
      // the block above left the flag stuck true off the products page, so
      // checking a brand never filled/emptied its sub-brands.
      setTimeout(() => {
        this.stopWatcherPropagation = false
      }, 100)
    },

    onIcqiInventoryDropdownChange (e) {
      this.params.criteria = e
    },

    onIcqiInventoryInputBlurred (e) {
      this.params.quantity = e
    },

    /**
     * Applies the current selection: navigate to the products listing with the
     * selected filters as query params. `applied` lets a host (the new panel)
     * close itself; it is inert for the legacy dropdown, which has no listener.
     */
    applyFilters () {
      if (this.params.criteria === 'set') {
        delete this.params.criteria
      }
      this.$router.push({
        name: 'products',
        query:
          Object.fromEntries(
            Object.entries(this.params).filter(([key, value]) => {
              return value !== null && !(Array.isArray(value) && value.length === 0)
            })
          )
      })
      EventBus.$emit('close-product-popper')
      this.$emit('applied')
    },

    /**
     * Resets the panel's selections without navigating. Guarded like
     * identifyFilters so the reset does not trip the brand/sub-brand sync
     * watchers.
     */
    clearFilters () {
      this.stopWatcherPropagation = true
      this.params = {
        brand: [],
        sub_brand: [],
        department: [],
        category: [],
        availability: [],
        color: [],
        pattern: [],
        season: [],
        criteria: 'set',
        quantity: null,
        size: []
      }
      setTimeout(() => {
        this.stopWatcherPropagation = false
      }, 100)
    },

    handleParentCheckboxChange (newSubBrand, oldSubBrand) {
      const removedIds = oldSubBrand.filter(x => !newSubBrand.includes(x))
      const addIds = newSubBrand.filter(x => !oldSubBrand.includes(x))
      let brandIds = new Set()
      if (removedIds.length) {
        this.filtersData.sub_brand.filter(subBrand => {
          if (removedIds.find(element => element == subBrand.id)) {
            brandIds.add(subBrand.parent_id)
          }
        })
        const uniqueBrandIds = Array.from(brandIds)
        const subBrandIds = this.filtersData.sub_brand
          .filter(subBrand => subBrand.parent_id === uniqueBrandIds[0])
          .map(subBrand => { return subBrand.id })
        const subBrandsNotIncluded = subBrandIds.every(subBrandId => !this.params.sub_brand.find(element => element == subBrandId))
        if (subBrandsNotIncluded) {
          this.params.brand = this.params.brand.filter(value => !uniqueBrandIds.find(element => element == value))
        }
      } else if (addIds.length) {
        this.filtersData.sub_brand.filter(subBrand => {
          if (addIds.find(element => element == subBrand.id)) {
            brandIds.add(subBrand.parent_id)
          }
        })
        const uniqueBrandId = Array.from(brandIds)[0]
        const subBrandIds = this.filtersData.sub_brand
          .filter(subBrand => subBrand.parent_id === uniqueBrandId)
          .map(subBrand => { return subBrand.id })
        const anySubBrandIncluded = this.params.sub_brand.some(subBrandId => subBrandIds.find(element => element == subBrandId))
        if (anySubBrandIncluded && !this.params.brand.find(element => element == uniqueBrandId)) {
          this.params.brand.push(uniqueBrandId)
        }
      }
      setTimeout(() => {
        this.stopWatcherPropagation = false
      }, 100)
    },

    handleChildCheckboxChange (newBrand, oldBrand) {
      const removedIds = oldBrand.filter(x => !newBrand.includes(x))
      const addIds = newBrand.filter(x => !oldBrand.includes(x))

      // Flat list of sub-brand ids belonging to the given brand ids. Loose ==
      // tolerates the number/string id mix that arrives from the query string.
      const subIdsOfBrands = (brandIds) => (this.filtersData?.brand || [])
        .filter(brand => brandIds.some(id => id == brand.id))
        .reduce((acc, brand) => acc.concat((brand.sub_brands || []).map(sub => sub.id)), [])

      if (removedIds.length > 0) {
        // Drop only the unchecked brand's sub-brands; keep every other brand's.
        const removedSubIds = subIdsOfBrands(removedIds)
        this.params.sub_brand = this.params.sub_brand.filter(id => !removedSubIds.some(rid => rid == id))
      } else if (addIds.length > 0) {
        // Add the checked brand's sub-brands, skipping any already selected.
        const addedSubIds = subIdsOfBrands(addIds)
        const merged = [...this.params.sub_brand]
        addedSubIds.forEach(id => {
          if (!merged.some(existing => existing == id)) merged.push(id)
        })
        this.params.sub_brand = merged
      }
      setTimeout(() => {
        this.stopWatcherPropagation = false
      }, 100)
    }
  }
}
